import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";

const ATMOSPHERE_MODES = new Set(["off", "lightning", "starfield", "rain"]);

const Ctx = React.createContext({
  background_image_url: null,
  use_background_image: false,
  site_flags: { live_status_enabled: false, ads_enabled: false },
  atmosphere_mode: "off",
});

export function BrandingProvider({ children }) {
  const [b, setB] = useState({
    background_image_url: null,
    use_background_image: false,
    site_flags: { live_status_enabled: false, ads_enabled: false },
    atmosphere_mode: "off",
  });
  const refresh = () => api.get("/branding", { headers: { "Cache-Control": "no-cache" } }).then((r) => setB((current) => ({ ...current, ...(r.data || {}) }))).catch(() => {});
  useEffect(() => {
    let active = true;
    const load = () => { if (document.visibilityState === "visible" && active) refresh(); };
    load();
    window.addEventListener("focus", load);
    document.addEventListener("visibilitychange", load);
    const interval = window.setInterval(load, 60000);
    return () => { active = false; window.removeEventListener("focus", load); document.removeEventListener("visibilitychange", load); window.clearInterval(interval); };
  }, []);
  return <Ctx.Provider value={b}>{children}</Ctx.Provider>;
}

export function useBranding() {
  return React.useContext(Ctx);
}

export function useSiteFlags() {
  const { site_flags } = React.useContext(Ctx);
  return site_flags || { live_status_enabled: false, ads_enabled: false };
}

// Single-layer canvas starfield: pale gold-white points travel outward from a
// distant vanishing point toward the viewer, covering the full viewport at
// every moment (perspective-projected from the actual canvas dimensions, not
// a fixed-fraction offset) with a slow, cruising pace. Near stars drift
// slightly faster than far ones for a gentle parallax cue. Original design,
// no Star Wars references.
function StarfieldCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const STAR_COUNT = 300;
    let stars = [];
    let width = 0;
    let height = 0;
    let cx = 0;
    let cy = 0;
    let raf = null;

    // Each star's world-space x/y is a fixed offset (in pixels, spanning the
    // full canvas) from the vanishing point; only z (depth) changes over
    // time. Dividing world x/y by z is what spreads a star from the center
    // outward toward the edges as it "approaches" — at spawn (z close to 1)
    // the star already sits anywhere across the full viewport, so the field
    // never collapses into a small cluster.
    const makeStar = () => ({
      wx: (Math.random() * 2 - 1) * Math.max(width, 1) * 0.5,
      wy: (Math.random() * 2 - 1) * Math.max(height, 1) * 0.5,
      z: 0.18 + Math.random() * 0.82,
      o: 0.45 + Math.random() * 0.55,
    });

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      cx = width / 2;
      cy = height / 2;
      canvas.width = width;
      canvas.height = height;
    };

    const drawFrame = (advance) => {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, width, height);
      for (const star of stars) {
        if (advance) {
          // Slow, cruising pace: near stars (small z) drift a little faster
          // than far ones, but the overall crossing takes well over ten
          // seconds rather than a quick jump.
          star.z -= 0.00028 + (1 - star.z) * 0.0014;
          if (star.z <= 0.02) Object.assign(star, makeStar());
        }
        const sx = cx + star.wx / star.z;
        const sy = cy + star.wy / star.z;
        if (sx < -20 || sx > width + 20 || sy < -20 || sy > height + 20) continue;
        const size = Math.max(0.5, (1 - star.z) * 2.1);
        const alpha = star.o * Math.min(1, (1 - star.z) * 1.4 + 0.15);
        // Pale gold-white: #fbdf0d blended toward white so it reads as a
        // premium starfield tone rather than pure/saturated yellow.
        ctx.beginPath();
        ctx.fillStyle = `rgba(254, 247, 195, ${Math.max(0, Math.min(1, alpha))})`;
        ctx.arc(sx, sy, size, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    resize();
    stars = Array.from({ length: STAR_COUNT }, () => makeStar());
    window.addEventListener("resize", resize);

    if (reduced) {
      drawFrame(false);
    } else {
      const loop = () => {
        drawFrame(true);
        raf = window.requestAnimationFrame(loop);
      };
      raf = window.requestAnimationFrame(loop);
    }

    return () => {
      window.removeEventListener("resize", resize);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className="xs-atmosphere-starfield" aria-hidden="true" />;
}

// Single-layer canvas rain: short, fine droplets fall top-to-bottom with
// varied length/opacity/speed so the field reads as rain, not a repeating
// static pattern or scanline grid.
function RainCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const DROP_COUNT = 140;
    let drops = [];
    let width = 0;
    let height = 0;
    let raf = null;

    const makeDrop = (randomizeY) => ({
      x: Math.random() * 1.15 - 0.05,
      y: randomizeY ? Math.random() : -0.05,
      len: 8 + Math.random() * 14,
      speed: 5.5 + Math.random() * 6.5,
      o: 0.18 + Math.random() * 0.34,
      drift: 0.35 + Math.random() * 0.25,
    });

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    const drawFrame = (advance) => {
      ctx.clearRect(0, 0, width, height);
      ctx.lineCap = "round";
      for (const drop of drops) {
        if (advance) {
          drop.y += (drop.speed / height) * 0.016;
          if (drop.y > 1.05) Object.assign(drop, makeDrop(false));
        }
        const sx = drop.x * width + drop.drift * 12;
        const sy = drop.y * height;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(210, 224, 238, ${drop.o})`;
        ctx.lineWidth = 1;
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx - drop.drift * 4, sy + drop.len);
        ctx.stroke();
      }
    };

    resize();
    drops = Array.from({ length: DROP_COUNT }, () => makeDrop(true));
    window.addEventListener("resize", resize);

    if (reduced) {
      drawFrame(false);
    } else {
      const loop = () => {
        drawFrame(true);
        raf = window.requestAnimationFrame(loop);
      };
      raf = window.requestAnimationFrame(loop);
    }

    return () => {
      window.removeEventListener("resize", resize);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  return <canvas ref={canvasRef} className="xs-atmosphere-rain-canvas" aria-hidden="true" />;
}

export function BrandBackdrop() {
  const { atmosphere_mode } = useBranding();
  const [lightning, setLightning] = useState(false);
  // Background-image rendering is parked (see PROJECT_MAP.txt): the public
  // backdrop always stays pure black regardless of any stored branding image
  // fields, since upload/toggle controls are disabled in Admin → Branding.
  const mode = ATMOSPHERE_MODES.has(atmosphere_mode) ? atmosphere_mode : "off";

  useEffect(() => {
    if (mode !== "lightning" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const timers = new Set();
    const later = (callback, delay) => {
      const timer = window.setTimeout(() => {
        timers.delete(timer);
        callback();
      }, delay);
      timers.add(timer);
    };
    const scheduleFlash = () => {
      later(() => {
        setLightning(true);
        later(() => setLightning(false), 180);
        scheduleFlash();
      }, 16000 + Math.floor(Math.random() * 16000));
    };
    scheduleFlash();
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [mode]);

  return (
    <div className="xs-brand-backdrop" aria-hidden="true">
      <div className="xs-brand-wash" />
      {mode === "starfield" && <div className="xs-atmosphere xs-atmosphere-mode-starfield"><StarfieldCanvas /></div>}
      {mode === "rain" && <div className="xs-atmosphere xs-atmosphere-mode-rain"><RainCanvas /></div>}
      {mode === "lightning" && (
        <div className={`xs-atmosphere xs-atmosphere-mode-lightning${lightning ? " xs-atmosphere-flash" : ""}`}>
          <div className="xs-atmosphere-lightning" />
        </div>
      )}
    </div>
  );
}

export default function BrandedSection({ children, fallback = "#000", style = {}, ...rest }) {
  return (
    <section
      style={{
        position: "relative",
        background: fallback,
        ...style,
      }}
      data-testid="branded-section"
      {...rest}
    >
      {children}
    </section>
  );
}
